import React, { useEffect, useMemo, useState } from "react";
import { getMerchantInsights30d } from "../../state/merchantInsightsStore";
import "../../styles/merchantDashboard.css";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "";

function resolveInsightImageUrl(url) {
  if (!url) return null;

  const value = String(url).trim();

  if (value.startsWith("data:")) {
  return value;
}

if (value.startsWith("http://") || value.startsWith("https://")) {
  return optimizedImageUrl(value, { width: 900, quality: 78 });
}

  if (!API_BASE) return value;

  const base = API_BASE.replace(/\/$/, "");
  const path = value.startsWith("/") ? value : `/${value}`;

  return `${base}${path}`;
}


function Tooltip({ text }) {
  return (
    <span
      style={{
        marginLeft: 6,
        fontSize: 12,
        cursor: "help",
        opacity: 0.6,
      }}
      title={text}
    >
      ⓘ
    </span>
  );
}

function demandBadge(score) {
  if (score >= 12) return { label: "High", className: "pill pill--drift" };
  if (score >= 6) {
    return { label: "Building", className: "pill pill--reinforcement" };
  }
  return { label: "Early", className: "pill pill--level" };
}

function ComparisonBar({ demand = 0, coverage = 0 }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            color: "rgba(43,43,43,0.58)",
            marginBottom: 5,
          }}
        >
          <span>Demand</span>
          <span>{demand}%</span>
        </div>
        <div
          style={{
            height: 10,
            background: "rgba(43,43,43,0.08)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.max(0, Math.min(100, demand))}%`,
              height: "100%",
              background: "rgba(91,31,43,0.72)",
            }}
          />
        </div>
      </div>

      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            color: "rgba(43,43,43,0.58)",
            marginBottom: 5,
          }}
        >
          <span>Catalog</span>
          <span>{coverage}%</span>
        </div>
        <div
          style={{
            height: 10,
            background: "rgba(43,43,43,0.08)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.max(0, Math.min(100, coverage))}%`,
              height: "100%",
              background: "rgba(176,140,108,0.78)",
            }}
          />
        </div>
      </div>
    </div>
  );
}

function DemandOnlyBar({ demand = 0, tooltip = null }) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: "rgba(43,43,43,0.58)",
          marginBottom: 5,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          Demand
          {tooltip ? <Tooltip text={tooltip} /> : null}
        </span>
        <span>{demand}%</span>
      </div>

      <div
        style={{
          height: 10,
          background: "rgba(43,43,43,0.08)",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, demand))}%`,
            height: "100%",
            background: "rgba(91,31,43,0.72)",
          }}
        />
      </div>
    </div>
  );
}

function ChartLegend() {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        fontSize: 12,
        color: "rgba(43,43,43,0.64)",
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 999,
            background: "rgba(91,31,43,0.72)",
            display: "inline-block",
          }}
        />
        <span>Shopper demand</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 999,
            background: "rgba(176,140,108,0.78)",
            display: "inline-block",
          }}
        />
        <span>Catalog coverage</span>
      </div>
    </div>
  );
}

function swatchStyle(shade) {
  const named = {
    cream: "#F3EDE3",
    ivory: "#F6F1E7",
    oat: "#D9CFBE",
    sand: "#D6C1A3",
    stone: "#BEB4A7",
    taupe: "#9E8F7A",
    camel: "#B58A57",
    beige: "#D8C3A5",
    white: "#F8F8F6",
    black: "#191919",
    charcoal: "#3A3A3D",
    ink: "#222736",
    slate: "#6C7480",
    grey: "#8B8E95",
    gray: "#8B8E95",
    blue: "#7FA6D9",
    navy: "#21314F",
    denim: "#516C9D",
    indigo: "#334C8C",
    olive: "#7A7E4E",
    sage: "#A3B49B",
    khaki: "#9A8F68",
    green: "#6B8A6D",
    burgundy: "#6F2C3F",
    wine: "#7A3148",
    oxblood: "#5D1F27",
    red: "#B14A4A",
    rose: "#C68A95",
    blush: "#D9A8B2",
    pink: "#D79AAA",
    mocha: "#8A6B5A",
    chocolate: "#5B3A29",
    brown: "#7A5A46",
    tan: "#B48A64",
    silver: "#B7BBC2",
    "soft black": "#2F2F31",
    "dusty pink": "#C9A0A9",
    "powder blue": "#B9D3E8",
  };

  return {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "1px solid rgba(0,0,0,0.10)",
    background: named[String(shade || "").toLowerCase()] || "#E7E2DD",
  };
}

export default function MerchantLanding() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("market");
  const [catalogSort, setCatalogSort] = useState("demand_score");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await getMerchantInsights30d();
        if (!cancelled) setData(res);
      } catch (e) {
        console.error("[insights] load failed", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    totals,
    market_trends,
    catalog_vs_demand,
    catalog_product_insights,
    strategy_signals,
  } = data || {};

  const signalStrength = totals?.signalBase || 0;
  const hasStrongSignal = signalStrength >= 20;

  const signalContext = `${totals?.windowTryOns || 0} try-ons, ${
  totals?.windowPins || 0
} saves, ${totals?.windowEdits || 0} edits, and ${
  totals?.windowBags || 0
} bag adds in the last 30 days`;

  const thStyle = {
    textAlign: "left",
    fontSize: 12,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "rgba(43,43,43,0.58)",
    padding: "0 0 14px",
    fontWeight: 700,
  };

  const thStyleRight = {
    ...thStyle,
    textAlign: "right",
    whiteSpace: "nowrap",
    paddingRight: 14,
  };

  const tdStyle = {
    padding: "14px 0",
    verticalAlign: "middle",
  };

  const tdStyleRight = {
    ...tdStyle,
    textAlign: "right",
    color: "rgba(43,43,43,0.88)",
    fontWeight: 600,
    whiteSpace: "nowrap",
    paddingRight: 14,
  };

const sortedProductInsights = useMemo(() => {
  const rows = [...(catalog_product_insights || [])];

  rows.sort((a, b) => {
    if (catalogSort === "title") {
      return String(a?.title || "").localeCompare(String(b?.title || ""));
    }

    if (catalogSort === "category") {
      return String(a?.category || "").localeCompare(String(b?.category || ""));
    }

    return (b?.[catalogSort] || 0) - (a?.[catalogSort] || 0);
  });

  return rows;
}, [catalog_product_insights, catalogSort]);

  const topPerformers = useMemo(() => {
    return sortedProductInsights.slice(0, 3);
  }, [sortedProductInsights]);

  const earlySignals = useMemo(() => {
    return sortedProductInsights
      .filter((p) => (p?.demand_score || 0) > 0 && (p?.demand_score || 0) < 6)
      .slice(0, 3);
  }, [sortedProductInsights]);

  const assortmentGaps = useMemo(() => {
    return (catalog_vs_demand || []).filter(
      (r) => (r?.interestPct || 0) > (r?.coveragePct || 0)
    );
  }, [catalog_vs_demand]);

  const strongCoverage = useMemo(() => {
    return (catalog_vs_demand || []).filter(
      (r) => (r?.coveragePct || 0) >= (r?.interestPct || 0)
    );
  }, [catalog_vs_demand]);

  if (loading) {
    return (
      <div className="merchant-wrap">
        <div className="merchant-shell">
          <h2 style={{ color: "#2b2b2b" }}>Loading insights…</h2>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="merchant-wrap">
        <div className="merchant-shell">
          <h2 style={{ color: "#2b2b2b" }}>Couldn’t load insights</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="merchant-wrap">
      <div className="merchant-shell">
        <div className="merchant-hero">
          <div>
            <div className="merchant-eyebrow">Veya Insights</div>
            <h1>Customer Signal</h1>
            <p>
  Behavioural signal from try-ons, saves, edits, and bag adds — showing what shoppers are actively styling, saving, and moving toward.
</p>
          </div>

      <div className="merchant-meta">
  <span>{totals?.windowTryOns || 0} try-ons</span>
  <span>{totals?.windowPins || 0} saves</span>
  <span>{totals?.windowEdits || 0} edits</span>
  <span>{totals?.windowBags || 0} added to bag</span>
</div>
        </div>

        <div className="merchant-tabs">
          <button
            type="button"
            className={`merchant-tab ${tab === "market" ? "is-active" : ""}`}
            onClick={() => setTab("market")}
          >
            Market Trends
          </button>

          <button
            type="button"
            className={`merchant-tab ${tab === "catalog" ? "is-active" : ""}`}
            onClick={() => setTab("catalog")}
          >
            Catalog vs Demand
          </button>

          <button
            type="button"
            className={`merchant-tab ${tab === "strategy" ? "is-active" : ""}`}
            onClick={() => setTab("strategy")}
          >
            Opportunities
          </button>
        </div>

        {tab === "market" && (
          <>
            {hasStrongSignal ? (
              <div className="merchant-stack">
                <div className="merchant-card">
                  <div className="merchant-card__head">
                    <div className="merchant-card__title">
                      Market direction
                      <Tooltip text="A high-level summary of how shopper behaviour is trending, based on edits, saves, and try-ons." />
                    </div>
                  </div>
                  <div className="merchant-card__body">
  <div>{market_trends?.headline}</div>

  <div
    style={{
      marginTop: 14,
      fontSize: 14,
      lineHeight: 1.55,
      color: "rgba(43,43,43,0.62)",
    }}
  >
    Use this as a directional read on what shoppers are moving toward now — useful for merchandising, styling edits, and future buying decisions.
  </div>
</div>
                </div>

                {market_trends?.dominantLooks?.length > 0 && (
                  <div className="merchant-card">
                    <div className="merchant-card__head">
                      <div className="merchant-card__title">
                        Emerging looks
                        <Tooltip text="Grouped outfit patterns detected from how shoppers combine items and describe styles." />
                      </div>
                      <div className="merchant-card__sub">
  These are interpreted look clusters, supported by repeated shopper behaviour rather than a single action. Use them to understand the outfit stories shoppers are building around.
  Merchant read: consider whether your current range gives shoppers enough pieces to complete this look.
</div>
                    </div>

                    <div className="merchant-card__body">
                      <div style={{ display: "grid", gap: 18 }}>
{market_trends.dominantLooks.map((look, i) => (
<div
  key={i}
  className="merchant-look-row"
  style={{
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.15fr) 560px 150px",
      gap: 28,
      alignItems: "stretch",
      padding: "8px 0 10px",
    }}
  >
    <div
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <div
        className="insightitem__label"
        style={{
          marginBottom: 8,
          fontSize: 22,
          lineHeight: 1.15,
        }}
      >
        {look.name}
      </div>

    <div
  className="insightitem__hint"
  style={{
    marginBottom: 10,
    fontSize: 15,
    lineHeight: 1.45,
    color: "rgba(43,43,43,0.64)",
    maxWidth: 560,
  }}
>
  {look.summary}
</div>


      {!!look.examplePieces?.length && (
        <div
          style={{
            background: "rgba(251,247,242,0.6)",
            border: "1px solid rgba(43,43,43,0.04)",
            borderRadius: 16,
            padding: "16px 18px",
            marginBottom: 24,
            maxWidth: 565,
          }}
        >
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.55,
              color: "rgba(43,43,43,0.68)",
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: "rgba(91,31,43,0.9)",
              }}
            >
              Think:
            </span>{" "}
            {look.examplePieces.join(", ")}.
          </div>
        </div>
      )}

      {!!look.keyPieces?.length && (
        <>
          <div
            style={{
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(43,43,43,0.46)",
              fontWeight: 700,
              marginBottom: 12,
            }}
          >
            Common elements
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {look.keyPieces.map((piece, idx) => (
              <span
                key={idx}
                className="pill"
                style={{
                  background: "rgba(251,247,242,0.86)",
                  border: "1px solid rgba(43,43,43,0.05)",
                }}
              >
                {piece}
              </span>
            ))}
          </div>
        </>
      )}
    </div>

<div
  className="merchant-look-images"
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 190px",
        gap: 14,
        alignItems: "stretch",
      }}
    >
      {look.heroImage ? (
        <img
          src={resolveInsightImageUrl(look.heroImage)}
          alt=""
          style={{
            width: "100%",
            height: 364,
            objectFit: "cover",
            borderRadius: 16,
            border: "1px solid rgba(43,43,43,0.08)",
            background: "rgba(255,255,255,0.3)",
            display: "block",
          }}
        />
      ) : (
        <div />
      )}

<div
  style={{
    display: "grid",
    gridTemplateRows: "1fr 1fr",
    gap: 14,
  }}
>
  {(look.referenceProducts || []).slice(1, 3).map((ref, idx) =>
    ref?.image ? (
      <img
        key={idx}
        src={resolveInsightImageUrl(ref.image)}
        alt=""
        style={{
          width: "100%",
          height: 175,
          objectFit: "cover",
          borderRadius: 16,
          border: "1px solid rgba(43,43,43,0.08)",
          background: "rgba(255,255,255,0.3)",
          display: "block",
        }}
      />
    ) : (
      <div
        key={idx}
        style={{
          width: "100%",
          height: 175,
          borderRadius: 16,
          border: "1px solid rgba(43,43,43,0.08)",
          background: "rgba(255,255,255,0.18)",
        }}
      />
    )
  )}
</div>
    </div>

    <div
      style={{
        minWidth: 150,
        textAlign: "right",
        alignSelf: "center",
        paddingTop: 4,
      }}
    >
<div
  style={{
    fontSize: 11,
    color: "rgba(43,43,43,0.52)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
  }}
>
  Support
  <Tooltip text="A composite signal score showing how strongly this look is supported by shopper behaviour. It combines repeated outfit pairings, garment engagement, and shared styling traits. Higher means more consistent behavioural signal, not guaranteed sales." />
</div>

      <div
        className="insightitem__value"
        style={{
          fontSize: 28,
          lineHeight: 1,
          marginBottom: 18,
        }}
      >
        {look.supportScore}
      </div>

      <div style={{ marginTop: 6 }}>
      <DemandOnlyBar
  demand={Math.min(
    100,
    Math.round((look.supportScore / 20) * 100)
  )}
  tooltip="Directional visualisation of support strength. This is scaled from the support score to help compare looks — it is not a literal percentage of demand."
/>
      </div>
    </div>
  </div>
))}
                      </div>
                    </div>
                  </div>
                )}

                {market_trends?.palettes?.length > 0 && (
                  <div className="merchant-card">
                    <div className="merchant-card__head">
                      <div className="merchant-card__title">
                        Colour direction
                        <Tooltip text="Common colour groupings appearing across saved and styled looks." />
                      </div>
                      <div className="merchant-card__sub">
                        Palette groupings showing up most often in current
                        shopper signal.
                      </div>
                    </div>

                    <div className="merchant-card__body">
                      <div style={{ display: "grid", gap: 18 }}>
                        {market_trends.palettes.map((palette, i) => (
                          <div key={i}>
                            <div className="insightitem__label">
                              {palette.label}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                marginTop: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              {(palette.shades || []).slice(0, 5).map((shade, idx) => (
                                <div
                                  key={idx}
                                  title={shade}
                                  style={swatchStyle(shade)}
                                />
                              ))}
                            </div>

                            <div
                              className="insightitem__hint"
                              style={{ marginTop: 8 }}
                            >
                              {(palette.shades || []).join(", ")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {market_trends?.keySignals?.length > 0 && (
                  <div className="merchant-card">
                    <div className="merchant-card__head">
                      <div className="merchant-card__title">
                        Recurring style traits
                        <Tooltip text="Repeated descriptive traits detected across edits and styling language." />
                      </div>
                    </div>

                    <div className="merchant-card__body">
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {market_trends.keySignals.map((signal, i) => (
                          <span key={i} className="pill">
                            {signal}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Signal building
                    <Tooltip text="Not enough shopper activity yet to form reliable trends." />
                  </div>
                </div>
                <div className="merchant-card__body">
  Signal is still building. Early shopper activity will appear here as styling directions, palettes, and demand signals once enough behaviour has been recorded.
  <div
    style={{
      marginTop: 12,
      fontSize: 13,
      color: "rgba(43,43,43,0.58)",
      lineHeight: 1.5,
    }}
  >
    Current base: {signalContext}. This view becomes more reliable as shoppers save, try on, add to bag, and create edits.
  </div>
</div>
              </div>
            )}
          </>
        )}

        {tab === "catalog" && (
          <div className="merchant-stack">
            {catalog_vs_demand?.length > 0 && (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Demand vs catalog comparison
                    <Tooltip text="Aligned: within ±5%. Slight lead: ±5–15%. Larger gaps indicate stronger mismatch between demand and catalog coverage." />
                  </div>
                  <div className="merchant-card__sub">
                    This is the evidence view behind the assortment conclusions
                    below.
                  </div>
                </div>

                <div className="merchant-card__body">
                  <ChartLegend />

                  <div style={{ display: "grid", gap: 30 }}>
      {catalog_vs_demand.map((row, i) => (
  <div key={i}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 16,
        marginBottom: 12,
      }}
    >
      <div>
        <div className="insightitem__label">{row.label}</div>
        <div className="insightitem__hint">{row.note}</div>
      </div>

      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: "rgba(43,43,43,0.5)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Direction
        </div>
        <div style={{ marginTop: 5 }}>
          {(() => {
  const demand = row.interestPct || 0;
  const catalog = row.coveragePct || 0;
  const diff = demand - catalog;
  const absDiff = Math.abs(diff);

  if (absDiff <= 5) {
    return <span className="pill pill--level">Aligned</span>;
  }

  if (absDiff <= 15) {
    return diff > 0 ? (
      <span className="pill pill--drift">
        Demand slightly leading
      </span>
    ) : (
      <span className="pill pill--reinforcement">
        Catalog slightly leading
      </span>
    );
  }

  return diff > 0 ? (
    <span className="pill pill--drift">
      Demand leading
    </span>
  ) : (
    <span className="pill pill--reinforcement">
      Catalog leading
    </span>
  );
})()}
        </div>
      </div>
    </div>

    <ComparisonBar
      demand={row.interestPct || 0}
      coverage={row.coveragePct || 0}
    />
  </div>
))}
                  </div>
                </div>
              </div>
            )}

            {sortedProductInsights?.length > 0 ? (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Catalog activity
                    <Tooltip text="Shows shopper interaction per product. Try-ons come from fitting room starts, saves come from product saves, edits count how often a product appears inside shopper edits, and demand signal is a weighted engagement score rather than a sales prediction." />
                  </div>
<div className="merchant-card__sub">
  Product-level shopper engagement across your current catalog.
</div>

<div
  style={{
    marginTop: 12,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  }}
>
  <button
    type="button"
    className={`pill ${catalogSort === "demand_score" ? "pill--drift" : ""}`}
    onClick={() => setCatalogSort("demand_score")}
  >
    Sort: Demand
  </button>

  <button
    type="button"
    className={`pill ${catalogSort === "try_ons" ? "pill--drift" : ""}`}
    onClick={() => setCatalogSort("try_ons")}
  >
    Try-ons
  </button>

  <button
    type="button"
    className={`pill ${catalogSort === "saves" ? "pill--drift" : ""}`}
    onClick={() => setCatalogSort("saves")}
  >
    Saves
  </button>

  <button
    type="button"
    className={`pill ${catalogSort === "edits" ? "pill--drift" : ""}`}
    onClick={() => setCatalogSort("edits")}
  >
    Edits
  </button>

  <button
  type="button"
  className={`pill ${catalogSort === "bags" ? "pill--drift" : ""}`}
  onClick={() => setCatalogSort("bags")}
>
  Bag adds
</button>

  <button
    type="button"
    className={`pill ${catalogSort === "category" ? "pill--drift" : ""}`}
    onClick={() => setCatalogSort("category")}
  >
    Category
  </button>

  <button
    type="button"
    className={`pill ${catalogSort === "title" ? "pill--drift" : ""}`}
    onClick={() => setCatalogSort("title")}
  >
    Title
  </button>
</div>
                </div>

                <div
                  className="merchant-card__body"
                  style={{ overflowX: "auto" }}
                >
                  <div
  style={{
    fontSize: 12,
    color: "rgba(43,43,43,0.62)",
    marginBottom: 14,
  }}
>
  Sorted by{" "}
  <strong>
    {catalogSort === "demand_score"
      ? "demand signal"
      : catalogSort === "try_ons"
      ? "try-ons"
      : catalogSort === "saves"
      ? "saves"
      : catalogSort === "edits"
      ? "edit appearances"
      : catalogSort === "bags"
      ? "bag adds"
      : catalogSort}
  </strong>
</div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      minWidth: 1040,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>
                          Product
                          <Tooltip text="Your catalog product." />
                        </th>
                        <th style={thStyleRight}>
                          Try-ons
                          <Tooltip text="How many times shoppers started trying this item on in the fitting room." />
                        </th>
                        <th style={thStyleRight}>
                          Saves
                          <Tooltip text="How many times shoppers saved this item." />
                        </th>
                        <th style={thStyleRight}>
  Edits
  <Tooltip text="How many times this product appeared inside shopper edits." />
</th>
<th style={{ ...thStyleRight, width: 72, minWidth: 72 }}>
  Bag
  <Tooltip text="How many times shoppers added this product to their bag (strong purchase intent signal)." />
</th>
<th style={{ ...thStyle, minWidth: 360, paddingLeft: 26 }}>
  Often styled with
  <Tooltip text="The products most often appearing alongside this item inside shopper edits." />
</th>
<th style={thStyleRight}>
  Demand signal
  <Tooltip text="A relative engagement indicator built from try-ons, saves, edits, and bag adds. Bag adds indicate strongest purchase intent." />
</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedProductInsights.map((row) => {
                        const badge = demandBadge(row.demand_score || 0);

                        return (
                          <tr
                            key={row.product_id}
                            style={{
                              borderTop: "1px solid rgba(43,43,43,0.08)",
                            }}
                          >
                            <td style={tdStyle}>
                              <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
    maxWidth: 300,
  }}
>
                                {row.image ? (
                                  <img
                                    src={resolveInsightImageUrl(row.image)}
                                    alt={row.title || ""}
                                    style={{
                                      width: 52,
                                      height: 66,
                                      objectFit: "cover",
                                      borderRadius: 10,
                                      border: "1px solid rgba(43,43,43,0.08)",
                                      background: "rgba(255,255,255,0.4)",
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 52,
                                      height: 66,
                                      borderRadius: 10,
                                      border: "1px solid rgba(43,43,43,0.08)",
                                      background: "rgba(255,255,255,0.25)",
                                      flexShrink: 0,
                                    }}
                                  />
                                )}

                                <div>
                                  <div
  className="insightitem__label"
  style={{ lineHeight: 1.25 }}
>
  {row.title || "Untitled product"}
</div>
                                </div>
                              </div>
                            </td>
                            <td style={tdStyleRight}>{row.try_ons ?? 0}</td>
<td style={tdStyleRight}>{row.saves ?? 0}</td>
<td style={tdStyleRight}>{row.edits ?? 0}</td>
<td style={{ ...tdStyleRight, width: 72, minWidth: 72 }}>
  {row.bags ?? 0}
</td>

<td style={{ ...tdStyle, minWidth: 360, paddingLeft: 26 }}>
  {row.top_pairings?.length > 0 ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {row.top_pairings.slice(0, 2).map((pair) => (
        <div
  key={pair.product_id}
  style={{
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  }}
>
          <span
  style={{
    color: "rgba(43,43,43,0.82)",
    fontWeight: 600,
    lineHeight: 1.35,
    minWidth: 0,
  }}
>
            {pair.title || "Untitled product"}
          </span>
          <span className="pill pill--level">
            {pair.count} edit{pair.count === 1 ? "" : "s"}
          </span>
        </div>
      ))}
    </div>
  ) : (
    <span style={{ color: "rgba(43,43,43,0.48)" }}>—</span>
  )}
</td>

<td style={tdStyleRight}>
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 10,
    }}
  >
    <span className={badge.className}>
      {badge.label}
    </span>
    <span className="insightitem__value">
      {row.demand_score ?? 0}
    </span>
  </div>
</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Catalog activity
                    <Tooltip text="This section will populate once products begin receiving try-ons, saves, or edit appearances." />
                  </div>
                </div>
                <div className="merchant-card__body">
                  No product-level activity has been recorded yet.
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "strategy" && (
          <div className="merchant-stack">
            {assortmentGaps?.length > 0 && (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Assortment gaps
                    <Tooltip text="Compares where shopper demand is forming versus how much of your catalog is covering that area." />
                  </div>
                </div>

                <div className="merchant-card__body">
                  <div className="insightlist">
                    {assortmentGaps.map((row, i) => (
                      <div key={i} className="insightitem">
                        <div>
                          <div className="insightitem__label">{row.label}</div>
                          <div className="insightitem__hint">
                            Shopper interest is building here, but your current
                            range is lighter. This could be an area to expand if
                            it aligns with your brand direction.
                          </div>
                        </div>
                        <div>
                          <span className="pill pill--drift">
                            Underrepresented
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {strongCoverage?.length > 0 && (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Strong coverage areas
                    <Tooltip text="Areas where your catalog depth is equal to or stronger than observed demand." />
                  </div>
                </div>

                <div className="merchant-card__body">
                  <div className="insightlist">
                    {strongCoverage.map((row, i) => (
                      <div key={i} className="insightitem">
                        <div>
                          <div className="insightitem__label">{row.label}</div>
                          <div className="insightitem__hint">
                            Your catalog is already well represented here. You
                            could reinforce this strength or maintain current
                            depth.
                          </div>
                        </div>
                        <div>
                          <span className="pill pill--reinforcement">
                            Well covered
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

      {(market_trends?.palettes?.length > 0 || market_trends?.dominantLooks?.length > 0) && (
  <div className="merchant-card">
    <div className="merchant-card__head">
      <div className="merchant-card__title">
        Emerging direction to consider
        <Tooltip text="A forward-looking read on the palette and styling direction currently gathering momentum in shopper behaviour." />
      </div>
      <div className="merchant-card__sub">
        This is less about a single product and more about what the broader signal suggests could be worth leaning into next.
      </div>
    </div>

    <div className="merchant-card__body">
      <div className="insightlist">
        {(() => {
          const topPalette = market_trends?.palettes?.[0] || null;
          const topLook = market_trends?.dominantLooks?.[0] || null;

          if (!topPalette && !topLook) return null;

          return (
            <div className="insightitem">
              <div>
                <div className="insightitem__label">
                  {topLook?.name
                    ? `${topLook.name} is gaining momentum`
                    : "A clearer direction is beginning to form"}
                </div>

                <div className="insightitem__hint">
                  {topPalette && topLook
                    ? `Shopper behaviour suggests growing pull toward ${String(topPalette.label || "").toLowerCase()} palettes and ${String(topLook.name || "").toLowerCase()} styling. This could be a useful area to explore through adjacent pieces, fresh colour depth, or stronger merchandising.`
                    : topLook
                    ? `Shopper behaviour suggests growing pull toward ${String(topLook.name || "").toLowerCase()} styling. This may be worth exploring through adjacent pieces and sharper outfit building.`
                    : `Shopper behaviour suggests growing pull toward ${String(topPalette?.label || "").toLowerCase()} palettes. This may be worth testing through adjacent colour depth and stronger merchandising.`}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginTop: 10,
                  }}
                >
                  {topPalette?.label ? (
                    <span className="pill pill--level">
                      Palette: {topPalette.label}
                    </span>
                  ) : null}

                  {topLook?.name ? (
                    <span className="pill pill--level">
                      Style: {topLook.name}
                    </span>
                  ) : null}

                  {(topLook?.keyPieces || []).slice(0, 2).map((piece, idx) => (
                    <span key={idx} className="pill pill--level">
                      Consider: {piece}
                    </span>
                  ))}
                </div>
              </div>

              <div className="insightitem__value">
                {topLook?.supportScore ?? "—"}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  </div>
)}

{topPerformers?.length > 0 && (
  <div className="merchant-card">
    <div className="merchant-card__head">
      <div className="merchant-card__title">
        Build around what shoppers are already pulling toward
        <Tooltip text="These are the products showing the strongest behavioural pull. The goal is not just to identify winners, but to understand what shoppers are building around them." />
      </div>
      <div className="merchant-card__sub">
        Strong products are more useful when read as anchors — what they pair with, what styling direction they sit inside, and what adjacent depth they suggest.
      </div>
    </div>

    <div className="merchant-card__body">
      <div className="insightlist">
        {topPerformers.map((p, i) => {
          const pairA = p.top_pairings?.[0]?.title || null;
          const pairB = p.top_pairings?.[1]?.title || null;

          const recommendation = pairA && pairB
            ? `People are repeatedly building around this piece with ${pairA} and ${pairB}. Consider deepening the surrounding outfit story, not just replenishing the hero product itself.`
            : pairA
            ? `People are repeatedly styling this with ${pairA}. This looks less like a one-off winner and more like an anchor piece you can build around.`
            : `This piece is drawing strong behavioural interest and is likely functioning as an anchor in your assortment.`;

          return (
            <div key={i} className="insightitem">
              <div>
                <div className="insightitem__label">{p.title}</div>

                <div className="insightitem__hint" style={{ marginTop: 4 }}>
                  {recommendation}
                </div>

                {p.top_pairings?.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 10,
                    }}
                  >
                    {p.top_pairings.slice(0, 2).map((pair) => (
                      <span key={pair.product_id} className="pill pill--level">
                        Pairing: {pair.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="insightitem__value">
                {p.demand_score}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
)}

            {earlySignals?.length > 0 && (
              <div className="merchant-card">
                <div className="merchant-card__head">
                  <div className="merchant-card__title">
                    Early signals to watch
                    <Tooltip text="Products with low but emerging interaction. These are not strong signals yet but may be worth monitoring." />
                  </div>
                </div>

                <div className="merchant-card__body">
                  <div className="insightlist">
                    {earlySignals.map((p, i) => (
                      <div key={i} className="insightitem">
                        <div>
                          <div className="insightitem__label">{p.title}</div>
                          <div className="insightitem__hint">
                            This product is starting to appear in shopper
                            activity, but the signal is still early.
                          </div>
                        </div>
                        <div className="insightitem__value">
                          {p.demand_score}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!sortedProductInsights?.length && !strategy_signals?.length && (
              <div className="merchant-card">
                <div className="merchant-card__body">
                  Strategic opportunities will become clearer as more shopper
                  activity is recorded.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}